import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Container from "../../../../CommonElements/Container";
import SectionHeading from "../../../../CommonElements/SectionHeading";
import OutgoingArrow from "../../../../CommonElements/OutgoingArrow";
import { getLeaderboardSlotClassName } from "../../../../Data/leaderboard";
import {
  STATIC_TICK_MS,
  tickStaticLeaderboard,
} from "../../../../Data/staticOverview";
import { fetchLeaderboard } from "../../../../utils/api";
import {
  getLeaderboardSocket,
  LEADERBOARD_SOCKET_EVENTS,
} from "../../../../utils/leaderboardSocket";
import { isStaticDataMode } from "../../../../utils/staticMode";
import { useLanguage } from "../../../../context/LanguageContext";
import useReveal from "../../../../hooks/useReveal";
import { resolveCountryFlag } from "../../../../utils/countryFlags";
import arrowRight from "../../../../assets/images/arrow-right.svg";

const PAGE_SIZE = 10;
const VISIBLE_PAGES = 5;
const TICK_ANIM_MS = 420;
const RANK_ARROW_MS = 1200;
const ROW_MOVE_MS = 480;

const TraderCell = ({ trader, nation, countryCode }) => {
  const { flagUrl } = resolveCountryFlag(nation, countryCode);

  return (
    <div className="data-table__trader">
      {flagUrl ? (
        <img
          className="data-table__trader-flag"
          src={flagUrl}
          alt=""
          width={32}
          height={32}
          loading="lazy"
        />
      ) : (
        <span className="data-table__avatar" aria-hidden="true" />
      )}
      <span className="data-table__trader-meta">
        <span className="data-table__trader-name">{trader}</span>
      </span>
    </div>
  );
};

const TickValue = ({ label, trend, className = "" }) => {
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    if (!trend) return undefined;
    setAnimating(true);
    const id = window.setTimeout(() => setAnimating(false), TICK_ANIM_MS);
    return () => window.clearTimeout(id);
  }, [label, trend]);

  const modifier = trend ? ` data-table__tick--motion-${trend}` : "";

  return (
    <span
      className={`data-table__tick${modifier}${
        animating ? " data-table__tick--animate" : ""
      }${className ? ` ${className}` : ""}`}
    >
      {label}
    </span>
  );
};

const RankChangeIcon = ({ direction }) => (
  <svg
    className="data-table__rank-change-icon"
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    aria-hidden="true"
  >
    {direction === "up" ? (
      <path d="M5 1.5L8.25 6H1.75L5 1.5Z" fill="currentColor" />
    ) : (
      <path d="M5 8.5L1.75 4H8.25L5 8.5Z" fill="currentColor" />
    )}
  </svg>
);

const RankCell = ({ rank, change }) => {
  const { t } = useLanguage();

  return (
  <span className="data-table__rank">
    <span>{rank}</span>
    <span className="data-table__rank-change-slot" aria-hidden={!change}>
      {change ? (
        <span
          className={`data-table__rank-change data-table__rank-change--${change}`}
          aria-label={change === "up" ? t("leaderboard.rankUp") : t("leaderboard.rankDown")}
        >
          <RankChangeIcon direction={change} />
        </span>
      ) : null}
    </span>
  </span>
  );
};

const ProfitLossCell = ({ value, label, trend }) => {
  const [flash, setFlash] = useState(false);
  const colorModifier = trend ?? (value >= 0 ? "up" : "down");

  useEffect(() => {
    if (!trend) return undefined;
    setFlash(true);
    const id = window.setTimeout(() => setFlash(false), TICK_ANIM_MS);
    return () => window.clearTimeout(id);
  }, [label, trend]);

  return (
    <div
      className={`data-table__profit data-table__profit--${colorModifier}${
        flash ? " data-table__profit--flash" : ""
      }`}
    >
      <span
        className={`data-table__profit-arrow data-table__profit-arrow--${colorModifier}`}
        aria-hidden="true"
      >
        <OutgoingArrow size={14} direction={colorModifier} />
      </span>
      <span className="data-table__profit-value">{label}</span>
    </div>
  );
};

const getVisiblePages = (page, totalPages) => {
  if (totalPages <= VISIBLE_PAGES) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  let start = Math.max(1, page - Math.floor(VISIBLE_PAGES / 2));
  let end = start + VISIBLE_PAGES - 1;

  if (end > totalPages) {
    end = totalPages;
    start = end - VISIBLE_PAGES + 1;
  }

  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
};

const normalizeApiRows = (rows) =>
  (Array.isArray(rows) ? rows : [])
    .filter((row) => row && (row.mt5Login != null || row.trader || row.id != null))
    .map((row, index) => ({
      id: row.id ?? row.mt5Login ?? `row-${index}`,
      trader: row.trader,
      nation: row.nation,
      equity: Number(row.equity),
      profitLoss: Number(row.profitLoss),
      performance: Number(row.performance),
      equityLabel: row.equityLabel,
      profitLossLabel: row.profitLossLabel,
      marginLabel: row.marginLabel,
      rank: row.rank,
      rankNumber: row.status === "disqualified" ? null : (row.rankNumber ?? index + 1),
      maxDrawdownPct: Number(row.maxDrawdownPct || 0),
      drawdownLabel: row.drawdownLabel || `${Number(row.drawdownPct || row.maxDrawdownPct || 0).toFixed(2)}%`,
      status: row.status || "active",
      disqualifiedReason: row.disqualifiedReason || "",
      disqualifiedAt: row.disqualifiedAt || null,
      roundTurnTrades: Number(row.roundTurnTrades || 0),
      mt5Login: row.mt5Login,
    }));

const Leaderboard = () => {
  const { t } = useLanguage();
  const [revealRef, inView] = useReveal(0.12);
  const [page, setPage] = useState(1);
  const [ranked, setRanked] = useState([]);
  const [loadState, setLoadState] = useState("loading"); // loading | ready | error
  const [rankChanges, setRankChanges] = useState({});
  const [cellTrends, setCellTrends] = useState({});
  const prevRanksRef = useRef({});
  const prevValuesRef = useRef({});
  const rowRefs = useRef(new Map());
  const rowPositionsRef = useRef(new Map());
  const bodyRef = useRef(null);
  const liveReadyRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let socket = null;
    let tickId = null;

    const applyPayload = (payload) => {
      if (cancelled || !payload || !Array.isArray(payload.rows)) return;
      const nextRows = normalizeApiRows(payload.rows);
      liveReadyRef.current = true;
      // Campaign-scoped payloads replace the list, including when that site has no rows.
      if (payload.campaignScoped) {
        setRanked(nextRows);
        setLoadState("ready");
        return;
      }
      // An empty update must not blank out rankings that are already on screen.
      setRanked((prev) => (nextRows.length || !prev.length ? nextRows : prev));
      setLoadState("ready");
    };

    const loadRestFallback = async () => {
      try {
        const { ok, payload } = await fetchLeaderboard();
        const result = payload?.result || {};
        if (cancelled) return;
        if (ok && Array.isArray(result.rows)) {
          applyPayload(result);
          return;
        }
        if (!liveReadyRef.current) {
          setRanked([]);
          setLoadState("error");
        }
      } catch {
        if (!cancelled && !liveReadyRef.current) {
          setRanked([]);
          setLoadState("error");
        }
      }
    };

    if (isStaticDataMode()) {
      const startStaticFeed = async () => {
        await loadRestFallback();
        if (cancelled || !liveReadyRef.current) return;

        // Simulate live socket ticks: equity moves, ranks swap, cell animations fire.
        tickId = window.setInterval(() => {
          if (cancelled) return;
          applyPayload({ rows: tickStaticLeaderboard() });
        }, STATIC_TICK_MS);
      };

      void startStaticFeed();

      return () => {
        cancelled = true;
        if (tickId != null) window.clearInterval(tickId);
      };
    }

    try {
      socket = getLeaderboardSocket();

      const onSnapshot = (payload) => applyPayload(payload);
      const onUpdate = (payload) => applyPayload(payload);
      const onConnect = () => {
        socket.emit(LEADERBOARD_SOCKET_EVENTS.subscribe, {
          landingUrl: window.location?.origin || "",
        });
      };
      const onConnectError = () => {
        void loadRestFallback();
      };

      socket.on(LEADERBOARD_SOCKET_EVENTS.snapshot, onSnapshot);
      socket.on(LEADERBOARD_SOCKET_EVENTS.update, onUpdate);
      socket.on("connect", onConnect);
      socket.on("connect_error", onConnectError);

      if (socket.connected) {
        onConnect();
      } else {
        // First paint via REST while socket connects — never seed/static rows.
        void loadRestFallback();
      }

      return () => {
        cancelled = true;
        socket.off(LEADERBOARD_SOCKET_EVENTS.snapshot, onSnapshot);
        socket.off(LEADERBOARD_SOCKET_EVENTS.update, onUpdate);
        socket.off("connect", onConnect);
        socket.off("connect_error", onConnectError);
        socket.emit(LEADERBOARD_SOCKET_EVENTS.unsubscribe);
      };
    } catch {
      void loadRestFallback();
      return () => {
        cancelled = true;
      };
    }
  }, []);

  useEffect(() => {
    const prev = prevRanksRef.current;
    const changes = {};

    ranked.forEach((row) => {
      const previous = prev[row.id];
      if (previous != null && row.rankNumber != null && previous !== row.rankNumber) {
        changes[row.id] = row.rankNumber < previous ? "up" : "down";
      }
    });

    prevRanksRef.current = Object.fromEntries(
      ranked.map((row) => [row.id, row.rankNumber])
    );

    if (Object.keys(changes).length) {
      setRankChanges(changes);
      const clearId = window.setTimeout(() => setRankChanges({}), RANK_ARROW_MS);
      return () => window.clearTimeout(clearId);
    }

    return undefined;
  }, [ranked]);

  useEffect(() => {
    const prev = prevValuesRef.current;
    const updates = {};

    ranked.forEach((row) => {
      const previous = prev[row.id];
      if (!previous) return;

      const trend = {};

      if (previous.equity !== row.equity) {
        trend.equity = row.equity > previous.equity ? "up" : "down";
      }
      if (previous.profitLoss !== row.profitLoss) {
        trend.profitLoss = row.profitLoss > previous.profitLoss ? "up" : "down";
      }
      if (previous.performance !== row.performance) {
        trend.margin = row.performance > previous.performance ? "up" : "down";
      }

      if (Object.keys(trend).length) {
        updates[row.id] = trend;
      }
    });

    prevValuesRef.current = Object.fromEntries(
      ranked.map((row) => [
        row.id,
        {
          equity: row.equity,
          profitLoss: row.profitLoss,
          performance: row.performance,
        },
      ])
    );

    if (Object.keys(updates).length) {
      setCellTrends((current) => {
        const next = { ...current };
        Object.entries(updates).forEach(([id, trend]) => {
          next[id] = { ...next[id], ...trend };
        });
        return next;
      });
    }
  }, [ranked]);

  const totalPages = Math.max(1, Math.ceil(ranked.length / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const rows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return ranked.slice(start, start + PAGE_SIZE);
  }, [page, ranked]);

  useEffect(() => {
    rowPositionsRef.current.clear();
  }, [page]);

  useLayoutEffect(() => {
    const bodyTop = bodyRef.current?.getBoundingClientRect().top ?? 0;

    rows.forEach((row) => {
      const el = rowRefs.current.get(row.id);
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const nextTop = rect.top - bodyTop;
      const prevTop = rowPositionsRef.current.get(row.id);

      if (prevTop != null) {
        const deltaY = prevTop - nextTop;

        if (Math.abs(deltaY) > 1) {
          el.style.willChange = "transform";
          el.style.transform = `translate3d(0, ${deltaY}px, 0)`;
          el.style.transition = "transform 0s";
          el.style.zIndex = "2";

          requestAnimationFrame(() => {
            el.style.transition = `transform ${ROW_MOVE_MS}ms ease`;
            el.style.transform = "translate3d(0, 0, 0)";
          });

          const onDone = (event) => {
            if (event.propertyName !== "transform") return;
            el.style.zIndex = "";
            el.style.transition = "";
            el.style.willChange = "";
            el.removeEventListener("transitionend", onDone);
          };
          el.addEventListener("transitionend", onDone);
        }
      }

      rowPositionsRef.current.set(row.id, nextTop);
    });
  }, [rows]);

  const visiblePages = getVisiblePages(page, totalPages);

  const setRowRef = (id) => (el) => {
    if (el) rowRefs.current.set(id, el);
    else rowRefs.current.delete(id);
  };

  return (
    <section
      className={`section section--white leaderboard${inView ? " is-in" : ""}`}
      id="leaderboard"
      ref={revealRef}
    >
      <Container>
        <SectionHeading title={t("leaderboard.title")} />

        <div className="leaderboard__table-wrap">
          <div className="data-table data-table--grid leaderboard__table" role="table">
            <div className="data-table__header" role="row">
              <div className="data-table__cell data-table__cell--head" role="columnheader">
                {t("leaderboard.rank")}
              </div>
              <div className="data-table__cell data-table__cell--head" role="columnheader">
                {t("leaderboard.trader")}
              </div>
              <div className="data-table__cell data-table__cell--head" role="columnheader">
                {t("leaderboard.equity")}
              </div>
              <div className="data-table__cell data-table__cell--head" role="columnheader">
                {t("leaderboard.profitLoss")}
              </div>
              <div className="data-table__cell data-table__cell--head" role="columnheader">
                {t("leaderboard.margin")}
              </div>
              <div className="data-table__cell data-table__cell--head" role="columnheader">
                {t("leaderboard.drawdown")}
              </div>
            </div>

            <div className="data-table__body" ref={bodyRef} role="rowgroup">
              {loadState === "loading" && !rows.length ? (
                <div className="data-table__status" role="status">
                  {t("leaderboard.loading")}
                </div>
              ) : null}
              {loadState !== "loading" && !rows.length ? (
                <div className="data-table__status" role="status">
                  {loadState === "error"
                    ? t("leaderboard.loadError")
                    : t("leaderboard.empty")}
                </div>
              ) : null}
              {rows.map((_, index) => (
                <div
                  key={`slot-${index}`}
                  className={`data-table__slot ${getLeaderboardSlotClassName(index)}`}
                  style={{ gridRow: index + 1 }}
                  aria-hidden="true"
                />
              ))}
              {rows.map((row, index) => {
                const trends = cellTrends[row.id] || {};

                return (
                  <div
                    key={row.id}
                    ref={setRowRef(row.id)}
                    className={`data-table__row ${
                      row.status === "disqualified"
                        ? "data-table__row--disqualified"
                        : row.rankNumber <= 3
                          ? "data-table__row--top"
                          : "data-table__row--other"
                    }`}
                    style={{ gridRow: index + 1 }}
                    role="row"
                  >
                    <div
                      className="data-table__cell data-table__rank-cell"
                      role="cell"
                    >
                      <RankCell rank={row.rank} change={rankChanges[row.id]} />
                    </div>
                    <div className="data-table__cell" role="cell">
                      <TraderCell
                        trader={row.trader}
                        nation={row.nation}
                      />
                    </div>
                    <div className="data-table__cell data-table__equity" role="cell">
                      <TickValue label={row.equityLabel} trend={trends.equity} />
                    </div>
                    <div
                      className="data-table__cell data-table__profit-cell"
                      role="cell"
                    >
                      <ProfitLossCell
                        value={row.profitLoss}
                        label={row.profitLossLabel}
                        trend={trends.profitLoss}
                      />
                    </div>
                    <div className="data-table__cell data-table__margin" role="cell">
                      <TickValue label={row.marginLabel} trend={trends.margin} />
                    </div>
                    <div className="data-table__cell" role="cell">
                      {row.drawdownLabel}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div
          className="pagination"
          hidden={ranked.length <= PAGE_SIZE}
          aria-label={t("leaderboard.pagination")}
        >
          <button
            type="button"
            className="pagination__nav"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            aria-label={t("leaderboard.previousPage")}
          >
            <span className="pagination__arrow pagination__arrow--prev">
              <img src={arrowRight} alt="" width={17} height={17} />
            </span>
            <span className="pagination__nav-label">{t("leaderboard.previous")}</span>
          </button>

          <div className="pagination__pages">
            {visiblePages.map((pageNumber) => (
              <button
                type="button"
                key={pageNumber}
                className={`pagination__page ${
                  pageNumber === page ? "pagination__page--active" : ""
                }`}
                onClick={() => setPage(pageNumber)}
              >
                {pageNumber}
              </button>
            ))}
          </div>

          <button
            type="button"
            className="pagination__nav"
            disabled={page === totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            aria-label={t("leaderboard.nextPage")}
          >
            <span className="pagination__nav-label">{t("leaderboard.next")}</span>
            <span className="pagination__arrow pagination__arrow--next">
              <img src={arrowRight} alt="" width={17} height={17} />
            </span>
          </button>
        </div>
      </Container>
    </section>
  );
};

export default Leaderboard;
